import React from 'react';
import { Plus, Trash, Heart, MusicNote, FileText, Translate } from '@phosphor-icons/react';

export default function ShayariModule({ metadata, onChange }) {
  const handleChange = (field, value) => {
    onChange({ ...metadata, [field]: value });
  };

  const handleCoupletChange = (index, value) => {
    const newCouplets = [...(metadata.couplets || [])];
    newCouplets[index] = value;
    handleChange('couplets', newCouplets);
  };

  const addCouplet = () => {
    handleChange('couplets', [...(metadata.couplets || []), '']);
  };

  const removeCouplet = (index) => {
    const newCouplets = (metadata.couplets || []).filter((_, i) => i !== index);
    handleChange('couplets', newCouplets);
  };

  return (
    <div className="space-y-6">
      {/* Couplets (Sher) */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium flex items-center gap-2">
            <Heart size={16} weight="fill" className="text-red-500" />
            Couplets (शेर) <span className="text-red-500">*</span>
          </label>
          <button
            onClick={addCouplet}
            className="flex items-center gap-1 px-3 py-1 text-sm rounded-lg bg-primary text-primary-foreground hover:opacity-90"
          >
            <Plus size={14} weight="bold" />
            Add Couplet
          </button>
        </div>

        {(metadata.couplets || []).length === 0 && (
          <button
            onClick={addCouplet}
            className="w-full p-8 border-2 border-dashed border-border rounded-lg hover:border-primary transition-colors"
          >
            <div className="text-center">
              <Heart size={32} className="mx-auto mb-2 text-muted-foreground" weight="fill" />
              <p className="text-sm text-muted-foreground">लिखें अपनी पहली शेर</p>
            </div>
          </button>
        )}

        <div className="space-y-4">
          {(metadata.couplets || []).map((couplet, index) => (
            <div key={index} className="relative group">
              <div className="absolute -left-8 top-2 text-xs text-muted-foreground font-mono">
                {index + 1}
              </div>
              <textarea
                value={couplet}
                onChange={(e) => handleCoupletChange(index, e.target.value)}
                placeholder="शेर लिखें (दो पंक्तियाँ)..."
                className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors resize-none text-lg"
                rows={3}
                style={{ fontFamily: 'Noto Sans Devanagari, serif', lineHeight: '2' }}
                dir="auto"
              />
              {(metadata.couplets || []).length > 1 && (
                <button
                  onClick={() => removeCouplet(index)}
                  className="absolute -right-10 top-2 p-2 rounded-lg text-red-500 opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-950/30 transition-all"
                >
                  <Trash size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Hindi Script */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <FileText size={16} />
          Hindi Script (देवनागरी)
        </label>
        <textarea
          value={metadata.hindi_script || ''}
          onChange={(e) => handleChange('hindi_script', e.target.value)}
          placeholder="हिंदी में लिखें..."
          className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors resize-none text-lg"
          rows={6}
          style={{ fontFamily: 'Noto Sans Devanagari, serif', lineHeight: '2' }}
          dir="auto"
        />
      </div>

      {/* Urdu Script (Optional) */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <FileText size={16} />
          Urdu Script (Optional)
        </label>
        <textarea
          value={metadata.urdu_script || ''}
          onChange={(e) => handleChange('urdu_script', e.target.value)}
          placeholder="اردو میں لکھیں..."
          className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors resize-none text-lg"
          rows={6}
          style={{ fontFamily: 'Noto Nastaliq Urdu, serif', lineHeight: '2' }}
          dir="rtl"
        />
      </div>

      {/* Transliteration */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <Translate size={16} />
          Transliteration (Roman Script)
        </label>
        <textarea
          value={metadata.transliteration || ''}
          onChange={(e) => handleChange('transliteration', e.target.value)}
          placeholder="Roman script transliteration..."
          className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors resize-none"
          rows={6}
        />
      </div>

      {/* Mood & Theme */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            Mood (मिज़ाज)
          </label>
          <select
            value={metadata.mood || ''}
            onChange={(e) => handleChange('mood', e.target.value)}
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
          >
            <option value="">Select mood...</option>
            <option value="romantic">Romantic (रोमानी)</option>
            <option value="melancholic">Melancholic (उदास)</option>
            <option value="passionate">Passionate (जोशीला)</option>
            <option value="philosophical">Philosophical (दार्शनिक)</option>
            <option value="satirical">Satirical (व्यंग्यात्मक)</option>
            <option value="devotional">Devotional (भक्ति)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">
            Theme (विषय)
          </label>
          <input
            type="text"
            value={metadata.theme || ''}
            onChange={(e) => handleChange('theme', e.target.value)}
            placeholder="e.g., प्रेम, दर्द, जुदाई"
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
          />
        </div>
      </div>

      {/* Poet's Notes */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Poet's Notes (शायर की टिप्पणी)
        </label>
        <textarea
          value={metadata.poet_notes || ''}
          onChange={(e) => handleChange('poet_notes', e.target.value)}
          placeholder="Share your thoughts about this shayari..."
          className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors resize-none"
          rows={3}
        />
      </div>

      {/* Audio Recitation */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <MusicNote size={16} />
          Audio Recitation URL
        </label>
        <input
          type="url"
          value={metadata.audio_url || ''}
          onChange={(e) => handleChange('audio_url', e.target.value)}
          placeholder="https://example.com/audio.mp3"
          className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Upload audio to a hosting service and paste the URL here
        </p>
      </div>
    </div>
  );
}
